import PropTypes from 'prop-types';

import {Box, Heading, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {
  hasHeroBannerBackgroundMedia,
  hasHeroBannerVideo,
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Callout Block - Overlay (CF-024)
 * Full-width background image/video with centered subtitle and CTA overlay
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "backgroundWrapper" | "backgroundMedia" | "contentWrapper" | "subtitle" | "actionLinks"} CalloutBlockOverlayParts
 * @typedef {Partial<Record<CalloutBlockOverlayParts, SystemStyleObject>>} CalloutBlockOverlayStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {CalloutBlockOverlayStyles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const CalloutBlockOverlayCF024 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, backgroundVideoMobile, subtitle, cta} = component;
  const mediaFields = {backgroundMedia, backgroundVideoMobile};
  const hasBackgroundMedia = hasHeroBannerBackgroundMedia(mediaFields);
  const hasVideo = hasHeroBannerVideo(mediaFields);
  const hasContentSection = Boolean(subtitle || cta);
  const isMobileNaturalVideo = hasVideo && !hasContentSection;

  const wrapperClassName = [
    'hero-banner',
    isMobileNaturalVideo && 'hero-banner--cf024-video-no-content'
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={wrapperClassName} {...styles.wrapper}>
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {hasBackgroundMedia ? (
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            mobileVideoOptions={isMobileNaturalVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined}
            {...styles.backgroundMedia}
          />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      <Box className="hero-content-wrapper" {...styles.contentWrapper}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <>
            {subtitle && <Heading {...styles.subtitle}>{subtitle}</Heading>}
            {cta && <Component component={cta} {...styles.actionLinks} />}
          </>
        )}
      </Box>
    </Box>
  );
};

CalloutBlockOverlayCF024.displayName = 'CalloutBlockOverlayCF024';
CalloutBlockOverlayCF024.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
